import { MatDialogConfig } from "@angular/material/dialog";
import { Tab, TabData, ViewStates } from "@app/shared/models/tab.model";
import { MenuOption, MenuAccess } from "@app/shared/models/menu.model";
import { ModalSheetService } from "@app/core/services/modal-sheet.service";
import { BaseMenuService } from "@app/core/services/base-menu.service";
import { MatBottomSheetConfig } from "@angular/material/bottom-sheet";
import { BreakpointService } from "./breakpoint.service";
import { Injectable, signal, inject } from "@angular/core";
import { ModalParms } from "@app/shared/models/modal.model";
import { Router } from "@angular/router";
import { ROUTES } from "@app/shared/constants/routes.constants";
import { MENU_OPTIONS } from "@app/shared/constants/menu-options.constants";
import { ICONS } from "@app/shared/constants/icons.constants";
import { ILH_MENU_OPTIONS } from "@app/portals/ilh-modules/ilh-shared/constants/menu-options.constants";
import { GLH_MENU_OPTIONS } from "@app/portals/glh-modules/glh-shared/constants/menu-options.constants";
import { WC_MENU_OPTIONS } from "@app/portals/wc-modules/wc-shared/constants/menu-options.constants";
import { APPLICATIONS } from "@app/shared/constants/applications.constants";

@Injectable({
  providedIn: "root"
})
export class MenuOptionService extends BaseMenuService {
  private modalSheetService = inject(ModalSheetService);
  private breakpointService = inject(BreakpointService);
  private router = inject(Router);

  /**
   * Open menu option by getting it's config by id and using the passed in module to create a component factory.
   * @param id
   * @param module : Lazy loaded module
   * @param component : Lazy loaded component or component name in module
   * @param data
   * @param legacyId : id of legacy menu item
   * @returns
   */
  openMenuOption<T>(internalMenuOption: MenuOption, component: T, data?: any, legacyId?: string) {
    const isLegacy: boolean = internalMenuOption?.isLegacy || internalMenuOption.id === MENU_OPTIONS.IFRAME.id;
    let menuOption: MenuOption = this.getMenuOption(isLegacy ? legacyId : internalMenuOption?.id);

    // Create a default menuOption if one not provided
    if (!menuOption) {
      menuOption = this._createDefaultMenuOptions(internalMenuOption);
    }

    // If we do not have a component
    // then the menu option is not properly configured
    if (!component) {
      const message = "ERR_SETUP_ERROR_MENU_OPTION_BAD_SETUP";
      this.notifyService.error("", message);
      return;
    }

    menuOption.component = component;

    // Check if multiple tabs allowed
    if (!this._canMultiTab(menuOption, data)) {
      return;
    }

    // If object has a value, then it is a menu item
    if (menuOption.object && menuOption.isLegacy) {
      if (!this.isLegacyAppOpen()) {
        // Try and find url for webfacing screen
        if (!menuOption?.applicationUrl) {
          menuOption.applicationUrl = this.getWebFaceUrl(menuOption.application);
          if (!menuOption?.applicationUrl) {
            const message = "ERR_MENU_OPTION_URL";
            this.notifyService.error("", message);
            return;
          }
        }

        this._addMenuComponent(menuOption, data);
      }
    } else {
      this._addMenuComponent(menuOption, data);
    }
  }

  /**
   * Route to lazy load module for menu option, this will trigger a guard to render the
   * component in a tab and prevent route url from changing.
   * @param menu
   * @param findMenuOption : used if we need to find the full menu object. Typically used when we only have the menu id
   * @param data
   */
  loadModernizedMenuOption(menu: MenuOption, findMenuOption?: boolean, data?: any) {
    if (findMenuOption) {
      menu = this.getMenuOption(menu.id);
    }

    this.router.navigate([`${ROUTES.HOME}/${menu.object}`], {
      skipLocationChange: true,
      state: {
        menu: menu,
        data: data
      }
    });
  }

  /**
   * Build and return array of modernized menu options
   * @param applicationId
   * @param isMenuOption : return list of only menu options
   */
  buildModernizedMenuOptionList(applicationId?: string) {
    let menuOptions: any = {};

    switch (applicationId) {
      case APPLICATIONS.INDIVIDUAL: {
        menuOptions = ILH_MENU_OPTIONS;
        break;
      }
      case APPLICATIONS.GROUP: {
        menuOptions = GLH_MENU_OPTIONS;
        break;
      }
      case APPLICATIONS.WC: {
        menuOptions = WC_MENU_OPTIONS;
        break;
      }
      default: {
        menuOptions = { ...ILH_MENU_OPTIONS, ...GLH_MENU_OPTIONS, ...WC_MENU_OPTIONS, ...MENU_OPTIONS };
      }
    }

    const componentList: MenuOption[] = [];
    Object.keys(menuOptions)?.forEach((key) => {
      const menu: MenuOption = menuOptions[key];
      // Only items with componentId allowed in list
      if (!menu?.componentId) {
        return;
      }
      componentList.push(menu);
    });

    return componentList;
  }

  /**
   * Check if multiple tabs allowed.
   * @param id
   * @param menuOption
   */
  private _canMultiTab(menuOption: MenuOption, data: any): boolean {
    if (this.tabService.isTabOpen(menuOption.id) && !menuOption.security.canMultiTab) {
      this.tabService.openTab(menuOption.id, null, data);
      return false;
    }
    return true;
  }

  /**
   * Create a default menu option object
   *
   * NOTE: User profiles will be set to multiTab.
   * @param id
   */
  private _createDefaultMenuOptions(menuOption: MenuOption): MenuOption {
    return {
      id: menuOption.id,
      componentId: menuOption.componentId,
      application: menuOption?.application ?? "core",
      module: menuOption?.module ?? "",
      group: menuOption?.group ?? "",
      itemDescription: {
        shortDescription: menuOption?.itemDescription?.shortDescription ?? "",
        longDescription: menuOption?.itemDescription?.longDescription ?? ""
      },
      applicationDescription: {
        shortDescription: menuOption?.applicationDescription?.shortDescription ?? "",
        longDescription: menuOption?.applicationDescription?.longDescription ?? ""
      },
      moduleDescription: {
        shortDescription: menuOption?.moduleDescription?.shortDescription ?? "",
        longDescription: menuOption?.moduleDescription?.longDescription ?? ""
      },
      groupDescription: {
        shortDescription: menuOption?.groupDescription?.shortDescription ?? "",
        longDescription: menuOption?.groupDescription?.longDescription ?? ""
      },
      object: menuOption?.object ?? undefined,
      isLegacy: false,
      security: {
        canAdd: menuOption?.security?.canAdd ?? true,
        canDelete: menuOption?.security?.canDelete ?? true,
        canEdit: menuOption?.security?.canEdit ?? true,
        canFavorite: menuOption?.security?.canFavorite ?? false,
        canMultiTab: menuOption?.security?.canMultiTab ?? false,
        canReport: menuOption?.security?.canReport ?? true,
        canView: menuOption?.security?.canView ?? true,
        isFavorite: menuOption?.security?.isFavorite ?? false,
        isLocked: menuOption?.security?.isLocked ?? false
      },
      icon: menuOption?.icon ?? ICONS.IFRAME
    };
  }

  /**
   * Add a menu component to tab array
   *
   * @param menuComponent
   * @param menuOption
   * @param data
   */
  private _addMenuComponent(menuOption: MenuOption, data: any): void {
    if (menuOption.application === "core") {
      this._checkMenuLocked(menuOption, data);
      return;
    }

    // Check if user can still access menu option
    const menuAccess: MenuAccess = { menuId: menuOption.id, hasAccess: false };

    this.canAccessMenuId(menuAccess).subscribe((menuAccess) => {
      if (menuAccess.hasAccess) {
        // Assign roleId if it exists
        menuOption.roleId = menuAccess?.roleId;
        this._checkMenuLocked(menuOption, data);
      }
    });
  }

  /**
   * Check if menu option is locked
   * @param menuComponent
   * @param menuOption
   * @param data
   */
  private _checkMenuLocked(menuOption: MenuOption, data: any): void {
    const menuTab = this._createNewTab(menuOption);

    // Set any parameters passed in
    menuTab.data.data = data;

    if (menuOption.security.isLocked) {
      // Set up dialog config and data
      const config = !this.breakpointService.isMobile() ? new MatDialogConfig() : new MatBottomSheetConfig();
      config.data = {
        title: menuOption.itemDescription.shortDescription
      } as ModalParms;

      this.fetchLockedMessage(menuOption.id).subscribe((resp) => {
        if (resp.message) {
          config.data.body = resp.message;
        }

        // Open login modal component and pass data
        this.modalSheetService.openLoginModal(config).subscribe((success) => {
          if (success) {
            this.tabService.addTab(menuTab);
          }
        });
      });
    } else {
      this.tabService.addTab(menuTab);
    }
  }

  /**
   * Create a new tab object for menu component
   *
   * @param menuComponent
   * @param menuOption
   */
  private _createNewTab(menuOption: MenuOption): Tab {
    const tab = new Tab(
      menuOption.id,
      menuOption?.itemDescription?.shortDescription,
      menuOption.component,
      menuOption.security.canFavorite,
      signal(false),
      menuOption?.isLegacy ? MENU_OPTIONS.IFRAME.id : menuOption.object,
      new TabData(menuOption, null, new ViewStates()),
      null,
      menuOption?.icon ?? ICONS.IFRAME,
      "",
      null,
      null,
      null,
      null,
      signal(false)
    );
    return tab;
  }
}
